import { useState } from "react"

import { PaymentFrequencyEnumOptions } from "@boundless/bl-api-client/customer/models/PaymentFrequencyEnumLabels"
import { SecurityEnumOptions } from "@boundless/bl-api-client/customer/models/SecurityEnumLabels"
import {
  getCustomerCompanyRetrieveQueryKey,
  getCustomerDebtScheduleListQueryKey,
  getCustomerRepositoryDebtScheduleRetrieveQueryKey,
  useCustomerDebtScheduleCreate,
  useCustomerDebtScheduleDestroy,
  useCustomerDebtScheduleList,
  useCustomerDebtScheduleUpdate,
  useCustomerRepositoryDebtScheduleRetrieve,
} from "@boundless/bl-api-client/customer/services"
import {
  CustomerDebtScheduleCreateBody,
  CustomerDebtScheduleUpdateBody,
} from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { Download } from "lucide-react"
import { useForm } from "react-hook-form"

import { DebtEntry } from "@/components/debt-table/DebtEntry"
import { DebtForm } from "@/components/debt-table/DebtForm"
import { DebtHeader } from "@/components/debt-table/DebtHeader"
import { DownloadButton } from "@/components/documents/DownloadButton"
import { FormHeader } from "@/components/form-elements/FormHeader"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { DebtSchedule } from "@boundless/bl-api-client/customer/models"
import type { z } from "zod"

type DebtTableProps = {
  companyUuid: string
}

export function DebtTable({ companyUuid }: DebtTableProps) {
  const queryClient = useQueryClient()

  const { data: debtsData } = useCustomerDebtScheduleList(companyUuid)

  const { data: debtScheduleDoc } =
    useCustomerRepositoryDebtScheduleRetrieve(companyUuid)

  const debtScheduleListQueryKey =
    getCustomerDebtScheduleListQueryKey(companyUuid)
  const debtScheduleDocQueryKey =
    getCustomerRepositoryDebtScheduleRetrieveQueryKey(companyUuid)
  // Row writes record a Debt Confirmation on the Company.
  const companyQueryKey = getCustomerCompanyRetrieveQueryKey(companyUuid)

  const [editingId, setEditingId] = useState<string | null>(null)

  const addForm = useForm<z.infer<typeof CustomerDebtScheduleCreateBody>>({
    resolver: zodResolver(CustomerDebtScheduleCreateBody),
    defaultValues: {
      currentBalance: "",
      lenderName: "",
      originalAmount: "",
      fundedDate: "",
      maturityDate: "",
      paymentAmount: "",
      paymentFrequency: PaymentFrequencyEnumOptions[3].value,
      security: SecurityEnumOptions[0].value,
    },
  })

  const editForm = useForm<z.infer<typeof CustomerDebtScheduleUpdateBody>>({
    resolver: zodResolver(CustomerDebtScheduleUpdateBody),
  })

  const startEditing = (debt: DebtSchedule) => {
    setEditingId(debt.uuid)
    editForm.reset({
      lenderName: debt.lenderName,
      originalAmount: debt.originalAmount,
      fundedDate: debt.fundedDate,
      maturityDate: debt.maturityDate,
      paymentAmount: debt.paymentAmount,
      paymentFrequency: debt.paymentFrequency,
      security: debt.security,
      currentBalance: debt.currentBalance,
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const { mutate: createDebt, isPending: isCreatingDebt } =
    useCustomerDebtScheduleCreate({
      mutation: {
        onError: (error: ErrorType<unknown>) => {
          applyErrorsToForm(
            addForm.setError,
            error as ErrorType<ErrorResponseType>,
          )
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: debtScheduleListQueryKey })
          queryClient.invalidateQueries({ queryKey: debtScheduleDocQueryKey })
          queryClient.invalidateQueries({ queryKey: companyQueryKey })
          addForm.reset()
        },
      },
    })
  const { mutate: updateDebt } = useCustomerDebtScheduleUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: debtScheduleListQueryKey })
        queryClient.invalidateQueries({ queryKey: debtScheduleDocQueryKey })
        queryClient.invalidateQueries({ queryKey: companyQueryKey })
        setEditingId(null)
      },
      onError: (error) => {
        applyErrorsToForm(
          editForm.setError,
          error as ErrorType<ErrorResponseType>,
        )
      },
    },
  })
  const { mutate: deleteDebt } = useCustomerDebtScheduleDestroy()

  const handleAddSubmit = (
    data: z.infer<typeof CustomerDebtScheduleCreateBody>,
  ) => {
    createDebt({
      companyUuid: companyUuid,
      data,
    })
  }

  const handleEditSubmit = (
    data: z.infer<typeof CustomerDebtScheduleUpdateBody>,
  ) => {
    const debtToUpdate = debtsData?.find((debt) => debt.uuid === editingId)
    if (debtToUpdate) {
      updateDebt({
        uuid: debtToUpdate.uuid,
        companyUuid: companyUuid,
        data,
      })
    }
  }

  const handleDelete = (uuid: string) => {
    deleteDebt(
      {
        uuid: uuid,
        companyUuid: companyUuid,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: debtScheduleListQueryKey })
          queryClient.invalidateQueries({ queryKey: debtScheduleDocQueryKey })
          queryClient.invalidateQueries({ queryKey: companyQueryKey })
        },
      },
    )
  }
  const downloadUrl = debtScheduleDoc?.downloadUrl
  const canDownload = debtsData?.length && downloadUrl

  return (
    <>
      <div className="flex items-center">
        <FormHeader title="Debt Table" />
        <DownloadButton
          aria-label="Download debt table"
          disabled={!canDownload}
          filename={debtScheduleDoc?.filename ?? "debt-schedule"}
          url={downloadUrl ?? ""}
          className="mb-4 ml-auto lg:mb-8"
        >
          <Download
            className={canDownload ? "text-primary" : "text-gray-400"}
          />
        </DownloadButton>
      </div>
      <div className="min-w-0 lg:overflow-x-auto">
        <div className="min-w-0 lg:min-w-5xl">
          <DebtHeader />
          <div className="flex flex-col">
            <Form {...addForm}>
              <form onSubmit={addForm.handleSubmit(handleAddSubmit)}>
                <DebtForm isPending={isCreatingDebt} />
              </form>
            </Form>

            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(handleEditSubmit)}>
                {debtsData?.map((debt) => (
                  <div key={debt.uuid} className="border-t border-gray-200">
                    {editingId === debt.uuid ? (
                      <DebtForm key={debt.uuid} cancelEdit={cancelEdit} />
                    ) : (
                      <DebtEntry
                        debt={debt}
                        startEditing={startEditing}
                        handleDelete={handleDelete}
                      />
                    )}
                  </div>
                ))}
              </form>
            </Form>
          </div>
        </div>
      </div>
    </>
  )
}
